import { type ICanCancel } from "@/api/WebApiClient.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import {
  cloneSelectionModel,
  emptySelectionModel,
  type ISelectionModel,
  setSelected,
} from "@/app-utils/selection-model.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import {
  type Attribute,
  type InputAttributeInput,
  type InputParentTypeInput,
  type InputSampleTypeInput,
  type ParentType,
  type RawAttributeDefault,
  type RawAttributeDefaultMapEntry,
  type SampleStatus,
  type SampleType,
  type StatusSet,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import {
  CONTROLLED_VOCABULARY_LIST_CHANGED,
  CREATE_ATTRIBUTE_FAILURE,
  CREATE_ATTRIBUTE_REQUEST,
  CREATE_ATTRIBUTE_SUCCESS,
  DELETE_ATTRIBUTES_FAILURE,
  DELETE_ATTRIBUTES_REQUEST,
  DELETE_ATTRIBUTES_SUCCESS,
  DELETE_PARENT_TYPES_FAILURE,
  DELETE_PARENT_TYPES_REQUEST,
  DELETE_PARENT_TYPES_SUCCESS,
  DELETE_SAMPLE_TYPES_FAILURE,
  DELETE_SAMPLE_TYPES_REQUEST,
  DELETE_SAMPLE_TYPES_SUCCESS,
  LOAD_ADMIN_PARENT_TYPES_FAILURE,
  LOAD_ADMIN_PARENT_TYPES_REQUEST,
  LOAD_ADMIN_PARENT_TYPES_SUCCESS,
  LOAD_ADMIN_SAMPLE_STATUSES_FAILURE,
  LOAD_ADMIN_SAMPLE_STATUSES_REQUEST,
  LOAD_ADMIN_SAMPLE_STATUSES_SUCCESS,
  LOAD_ADMIN_SAMPLE_TYPES_FAILURE,
  LOAD_ADMIN_SAMPLE_TYPES_REQUEST,
  LOAD_ADMIN_SAMPLE_TYPES_SUCCESS,
  LOAD_ATTRIBUTE_FAILURE,
  LOAD_ATTRIBUTE_REQUEST,
  LOAD_ATTRIBUTE_SUCCESS,
  LOAD_ATTRIBUTES_CLEAR,
  LOAD_ATTRIBUTES_FAILURE,
  LOAD_ATTRIBUTES_REQUEST,
  LOAD_ATTRIBUTES_SUCCESS,
  LOAD_PARENT_TYPE_FAILURE,
  LOAD_PARENT_TYPE_REQUEST,
  LOAD_PARENT_TYPE_SUCCESS,
  LOAD_SAMPLE_TYPE_FAILURE,
  LOAD_SAMPLE_TYPE_REQUEST,
  LOAD_SAMPLE_TYPE_SUCCESS,
  MODIFY_ADMIN_PARENT_TYPE_FAILURE,
  MODIFY_ADMIN_PARENT_TYPE_REQUEST,
  MODIFY_ADMIN_PARENT_TYPE_SUCCESS,
  MODIFY_ADMIN_SAMPLE_TYPE_FAILURE,
  MODIFY_ADMIN_SAMPLE_TYPE_REQUEST,
  MODIFY_ADMIN_SAMPLE_TYPE_SUCCESS,
  MODIFY_ATTRIBUTE_FAILURE,
  MODIFY_ATTRIBUTE_REQUEST,
  MODIFY_ATTRIBUTE_SUCCESS,
  PARENT_TYPE_SELECTION_CHANGE,
  PARENT_TYPE_SELECTION_RESET,
  SAMPLE_TYPE_SELECTION_CHANGE,
  SAMPLE_TYPE_SELECTION_RESET,
  SAVE_ADMIN_PARENT_TYPE_FAILURE,
  SAVE_ADMIN_PARENT_TYPE_REQUEST,
  SAVE_ADMIN_PARENT_TYPE_SUCCESS,
  SAVE_ADMIN_SAMPLE_TYPE_FAILURE,
  SAVE_ADMIN_SAMPLE_TYPE_REQUEST,
  SAVE_ADMIN_SAMPLE_TYPE_SUCCESS,
} from "@/redux/action-types.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import { type IInputVocabularyTerm } from "../../ui-modules/admin/ControlledValuesManager.js";
import { createReducer } from "../redux-utils.js";

/**
 * State definition
 */

export interface ISampleAdminState {
  isLoadingSampleTypes: boolean;
  isLoadingSampleStatuses: boolean;
  sampleTypes: SampleType[];
  sampleStatuses: StatusSet[];
  sampleTypesSelectionModel: ISelectionModel;

  isLoadingParentTypes: boolean;
  parentTypes: ParentType[];
  parentTypesSelectionModel: ISelectionModel;

  isLoadingAttributes: boolean;
  attributes: Attribute[];

  controlledValues: IInputVocabularyTerm[]; // when creating a sample attribute

  sampleType: SampleType; // for configuration of sample type or playbook sample registration configuration
  attributeDefaults: Partial<RawAttributeDefault>[]; // for the above sample type
  isLoadingSampleTypeWithAttributeDefaults: boolean;
}

const initialState: ISampleAdminState = {
  isLoadingSampleTypes: false,
  isLoadingSampleStatuses: false,
  sampleTypes: [],
  sampleStatuses: [],
  sampleTypesSelectionModel: { ...emptySelectionModel },

  isLoadingParentTypes: false,
  parentTypes: [],
  parentTypesSelectionModel: { ...emptySelectionModel },

  isLoadingAttributes: false,
  attributes: [],

  controlledValues: [],

  sampleType: null,
  attributeDefaults: [],
  isLoadingSampleTypeWithAttributeDefaults: false,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_ADMIN_SAMPLE_TYPES_REQUEST]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingSampleTypes: true };
  },
  [LOAD_ADMIN_SAMPLE_TYPES_FAILURE]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingSampleTypes: false };
  },
  [LOAD_ADMIN_SAMPLE_TYPES_SUCCESS]: (state: ISampleAdminState, sampleTypes: SampleType[]): ISampleAdminState => {
    return { ...state, isLoadingSampleTypes: false, sampleTypes };
  },
  [SAMPLE_TYPE_SELECTION_CHANGE]: (state: ISampleAdminState, payload): ISampleAdminState => {
    const { sampleType, selected } = payload;

    const newSelectionModel: ISelectionModel = cloneSelectionModel(state.sampleTypesSelectionModel);
    setSelected(newSelectionModel, sampleType.id, null, selected);

    return { ...state, sampleTypesSelectionModel: newSelectionModel };
  },
  [SAMPLE_TYPE_SELECTION_RESET]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, sampleTypesSelectionModel: emptySelectionModel };
  },

  [LOAD_ADMIN_SAMPLE_STATUSES_REQUEST]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingSampleStatuses: true };
  },
  [LOAD_ADMIN_SAMPLE_STATUSES_FAILURE]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingSampleStatuses: false };
  },
  [LOAD_ADMIN_SAMPLE_STATUSES_SUCCESS]: (state: ISampleAdminState, sampleStatuses: StatusSet[]): ISampleAdminState => {
    return { ...state, isLoadingSampleStatuses: false, sampleStatuses };
  },

  [LOAD_ADMIN_PARENT_TYPES_REQUEST]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingParentTypes: true };
  },
  [LOAD_ADMIN_PARENT_TYPES_FAILURE]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingParentTypes: false };
  },
  [LOAD_ADMIN_PARENT_TYPES_SUCCESS]: (state: ISampleAdminState, parentTypes: ParentType[]): ISampleAdminState => {
    return { ...state, isLoadingParentTypes: false, parentTypes };
  },
  [PARENT_TYPE_SELECTION_CHANGE]: (state: ISampleAdminState, payload): ISampleAdminState => {
    const { parentType, selected } = payload;

    const newSelectionModel: ISelectionModel = cloneSelectionModel(state.parentTypesSelectionModel);
    setSelected(newSelectionModel, parentType.id, null, selected);

    return { ...state, parentTypesSelectionModel: newSelectionModel };
  },
  [PARENT_TYPE_SELECTION_RESET]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, parentTypesSelectionModel: emptySelectionModel };
  },
  [CONTROLLED_VOCABULARY_LIST_CHANGED]: (
    state: ISampleAdminState,
    controlledValues: IInputVocabularyTerm[],
  ): ISampleAdminState => {
    return { ...state, controlledValues: controlledValues };
  },

  [LOAD_ATTRIBUTES_REQUEST]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingAttributes: true };
  },
  [LOAD_ATTRIBUTES_FAILURE]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingAttributes: false };
  },
  [LOAD_ATTRIBUTES_SUCCESS]: (state: ISampleAdminState, attributes: Attribute[]): ISampleAdminState => {
    return { ...state, isLoadingAttributes: false, attributes };
  },
  [LOAD_ATTRIBUTES_CLEAR]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingAttributes: false, attributes: [] };
  },

  [SAVE_ADMIN_SAMPLE_TYPE_SUCCESS]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state };
  },

  [LOAD_SAMPLE_TYPE_REQUEST]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingSampleTypeWithAttributeDefaults: true };
  },
  [LOAD_SAMPLE_TYPE_SUCCESS]: (state: ISampleAdminState, payload): ISampleAdminState => {
    return { ...state, isLoadingSampleTypeWithAttributeDefaults: false, ...payload };
  },
  [LOAD_SAMPLE_TYPE_FAILURE]: (state: ISampleAdminState): ISampleAdminState => {
    return { ...state, isLoadingSampleTypeWithAttributeDefaults: false };
  },
});

/**
 * Action creators
 */
export function loadSampleTypes() {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SampleType[]> => {
    dispatch({ type: LOAD_ADMIN_SAMPLE_TYPES_REQUEST });

    return webApiClient
      .fetchAdminSampleTypes()
      .then((sampleTypes: SampleType[]) => {
        dispatch({
          type: LOAD_ADMIN_SAMPLE_TYPES_SUCCESS,
          payload: sampleTypes,
        });
        return sampleTypes;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ADMIN_SAMPLE_TYPES_FAILURE,
          alertTitle: "Fetching of Sample Types Failed",
        }),
      );
  };
}

export function loadSampleStatusSets() {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<StatusSet[]> => {
    dispatch({ type: LOAD_ADMIN_SAMPLE_STATUSES_REQUEST });

    return webApiClient
      .fetchAdminSampleStatusSets()
      .then((sampleStatuses: SampleStatus[]) => {
        dispatch({
          type: LOAD_ADMIN_SAMPLE_STATUSES_SUCCESS,
          payload: sampleStatuses,
        });
        return sampleStatuses;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ADMIN_SAMPLE_STATUSES_FAILURE,
          alertTitle: "Fetching of Sample Statuses Failed",
        }),
      );
  };
}

export function loadParentTypes() {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<ParentType[]> => {
    dispatch({ type: LOAD_ADMIN_PARENT_TYPES_REQUEST });

    return webApiClient
      .fetchAdminParentTypes()
      .then((parentTypes: ParentType[]) => {
        dispatch({
          type: LOAD_ADMIN_PARENT_TYPES_SUCCESS,
          payload: parentTypes,
        });
        return parentTypes;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ADMIN_PARENT_TYPES_FAILURE,
          alertTitle: "Fetching of Parent Types Failed",
        }),
      );
  };
}

// needs another set of attribute properties e.g. vocabulary terms so that we can configure parent type attributes
export function loadParentTypesAvailableForSampleTypeCreation() {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<ParentType[]> => {
    dispatch({ type: LOAD_ADMIN_PARENT_TYPES_REQUEST });

    return webApiClient
      .fetchParentTypesForSampleTypeCreation()
      .then((parentTypes: ParentType[]) => {
        dispatch({
          type: LOAD_ADMIN_PARENT_TYPES_SUCCESS,
          payload: parentTypes,
        });
        return parentTypes;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ADMIN_PARENT_TYPES_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function loadFullParentTypeWithAttributes(parentTypeId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<ParentType> => {
    dispatch({ type: LOAD_PARENT_TYPE_REQUEST });

    return webApiClient
      .fetchAdminParentType(parentTypeId)
      .then((parentType: ParentType) => {
        dispatch({
          type: LOAD_PARENT_TYPE_SUCCESS,
          payload: parentType,
        });
        return parentType;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PARENT_TYPE_FAILURE,
          alertTitle: `Fetching of Parent Type ${parentTypeId} Failed`,
        }),
      );
  };
}

export function buildDefaultsMapByAttributeId(defaults: RawAttributeDefaultMapEntry[]): RawAttributeDefault[] {
  const attributeDefaults: RawAttributeDefault[] = []; // sparse array of attributeId => default
  defaults.forEach((v) => {
    if (!isEmptyDefault(v.data)) {
      attributeDefaults[v.attributeId] = v.data;
    }
  });
  return attributeDefaults;
}

export function fetchFullSampleTypeWithAttributeDefaults(sampleTypeId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_SAMPLE_TYPE_REQUEST });

    return Promise.all([
      webApiClient.fetchAdminSampleType(sampleTypeId), // response: ISampleType
      webApiClient.fetchRawSampleAttributeDefaults(sampleTypeId), // response: new: Array<IRawAttributeDefaultMapEntry> old: List<value-object-with-id>
    ])
      .then((values: any[]) => {
        const sampleType: SampleType = values[0];
        const attributeDefaults: Partial<RawAttributeDefault>[] = buildDefaultsMapByAttributeId(values[1]); // sparse array of attributeId => default
        dispatch({
          type: LOAD_SAMPLE_TYPE_SUCCESS,
          payload: { sampleType, attributeDefaults },
        });
        return { sampleType, attributeDefaults };
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_SAMPLE_TYPE_FAILURE,
          alertTitle: `Fetching of Sample Type ${sampleTypeId} Together With Attribute Defaults Failed`,
        }),
      );
  };
}

export function onSampleTypeSelectionChange(sampleType: SampleType, selected: boolean) {
  return {
    type: SAMPLE_TYPE_SELECTION_CHANGE,
    payload: {
      sampleType,
      selected,
    },
  };
}

export function clearSampleTypeSelection() {
  return {
    type: SAMPLE_TYPE_SELECTION_RESET,
  };
}

export function onParentTypeSelectionChange(parentType: ParentType, selected: boolean) {
  return {
    type: PARENT_TYPE_SELECTION_CHANGE,
    payload: {
      parentType,
      selected,
    },
  };
}

export function clearParentTypeSelection() {
  return {
    type: PARENT_TYPE_SELECTION_RESET,
  };
}

export function onControlledVocabularyListChanged(controlledValues: IInputVocabularyTerm[]) {
  return {
    type: CONTROLLED_VOCABULARY_LIST_CHANGED,
    payload: controlledValues,
  };
}

export function createAttribute(payload: InputAttributeInput) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Attribute> => {
    dispatch({ type: CREATE_ATTRIBUTE_REQUEST });

    return webApiClient
      .createAttribute(payload)
      .then((attribute: Attribute) => {
        dispatch({
          type: CREATE_ATTRIBUTE_SUCCESS,
          payload: attribute,
        });

        notify.info({
          title: selMessage({ id: "admin.sample.attribute.created.successTitle" }),
          children: SelMessage({
            id: "admin.sample.attribute.created.successMessage",
            values: { attributeName: attribute.attributeName },
          }),
        });

        return attribute;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_ATTRIBUTE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function modifyAttribute(payload: InputAttributeInput) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Attribute> => {
    dispatch({ type: MODIFY_ATTRIBUTE_REQUEST });

    return webApiClient
      .modifyAttribute(payload)
      .then((attribute: Attribute) => {
        dispatch({
          type: MODIFY_ATTRIBUTE_SUCCESS,
          payload: attribute,
        });

        notify.info({
          title: selMessage({ id: "admin.sample.attribute.modified.successTitle" }),
          children: SelMessage({
            id: "admin.sample.attribute.modified.successMessage",
            values: { attributeName: attribute.attributeName },
          }),
        });

        return attribute;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: MODIFY_ATTRIBUTE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function deleteAttributes(payload: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    dispatch({ type: DELETE_ATTRIBUTES_REQUEST, payload: payload });

    return webApiClient
      .deleteAttributes(payload)
      .then((success: boolean) => {
        dispatch({
          type: DELETE_ATTRIBUTES_SUCCESS,
          payload: success,
        });

        notify.info({
          title: selMessage({ id: "admin.sample.attribute.deleted.successTitle" }),
          children: SelMessage({
            id: "admin.sample.attribute.deleted.successMessage",
            values: { attributeCount: payload.length },
          }),
        });

        return success;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_ATTRIBUTES_FAILURE,
          showAlert: true,
          throwError: false,
          alertTitle: `Failed to Delete ${payload.length} ${pluralizeWord("attribute", payload.length)}`,
        }),
      );
  };
}

export function deleteSampleTypes(sampleTypes: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    dispatch({ type: DELETE_SAMPLE_TYPES_REQUEST, payload: sampleTypes });

    return webApiClient
      .deleteSampleTypes(sampleTypes)
      .then((success: boolean) => {
        dispatch({
          type: DELETE_SAMPLE_TYPES_SUCCESS,
        });
        notify.info({
          title: selMessage({ id: "admin.sample.type.deleted.successTitle" }),
          children: SelMessage({
            id: "admin.sample.type.deleted.successMessage",
            values: { sampleTypesCount: sampleTypes.length },
          }),
        });
        dispatch(loadSampleTypes());

        return success;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_SAMPLE_TYPES_FAILURE,
          alertTitle: "Failed to Delete Sample Types",
        }),
      );
  };
}

export function deleteParentTypes(parentTypes: number[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    dispatch({ type: DELETE_PARENT_TYPES_REQUEST, payload: parentTypes });

    return webApiClient
      .deleteParentTypes(parentTypes)
      .then((success: boolean) => {
        dispatch({
          type: DELETE_PARENT_TYPES_SUCCESS,
        });
        notify.info({
          title: selMessage({ id: "admin.sample.parent.deleted.successTitle" }),
          children: SelMessage({
            id: "admin.sample.parent.deleted.successMessage",
            values: { parentTypesCount: parentTypes.length },
          }),
        });
        dispatch(loadParentTypes());

        return success;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_PARENT_TYPES_FAILURE,
          alertTitle: "Failed to Delete Parent Types",
        }),
      );
  };
}

export function loadAttributes() {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Attribute[]> & ICanCancel => {
    dispatch({ type: LOAD_ATTRIBUTES_REQUEST });

    const request = webApiClient
      .fetchAttributes()
      .then((attributes: Attribute[]) => {
        dispatch({
          type: LOAD_ATTRIBUTES_SUCCESS,
          payload: attributes,
        });
        return attributes;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ATTRIBUTES_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
    return request;
  };
}

export function loadAttributesForDecoration() {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Attribute[]> & ICanCancel => {
    return webApiClient.fetchAttributesAsDecoration().catch(
      createErrorHandler(dispatch, {
        actionType: LOAD_ATTRIBUTES_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function clearAttributes() {
  return (dispatch, _getState, _webApiClient: WebApiClient): Promise<void> => {
    dispatch({
      type: LOAD_ATTRIBUTES_CLEAR,
      payload: null,
    });
    return;
  };
}

export function loadAttribute(attribute: Attribute) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Attribute> => {
    dispatch({ type: LOAD_ATTRIBUTE_REQUEST });

    return webApiClient
      .fetchAttribute(attribute.id)
      .then((att: Attribute) => {
        dispatch({
          type: LOAD_ATTRIBUTE_SUCCESS,
          payload: att,
        });
        return att;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ATTRIBUTE_FAILURE,
          alertTitle: `Could Not Fetch the Attribute of ID ${attribute.id}`,
        }),
      );
  };
}

export function validateSampleTypeExists(name: string) {
  return (_dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient.validateSampleTypeExists(name);
  };
}

export function saveNewSampleType(config: InputSampleTypeInput) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SampleType> => {
    dispatch({ type: SAVE_ADMIN_SAMPLE_TYPE_REQUEST });

    return webApiClient
      .saveNewSampleType(config)
      .then((sampleType: SampleType) => {
        dispatch({
          type: SAVE_ADMIN_SAMPLE_TYPE_SUCCESS,
          payload: sampleType,
        });
        notify.info({
          title: selMessage({ id: "admin.sample.type.created.successTitle" }),
          children: SelMessage({
            id: "admin.sample.type.created.successMessage",
            values: { sampleType: sampleType.name },
          }),
        });
        dispatch(loadSampleTypes());
        return sampleType;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SAVE_ADMIN_SAMPLE_TYPE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function modifySampleType(data: InputParentTypeInput) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<ParentType> => {
    dispatch({ type: MODIFY_ADMIN_SAMPLE_TYPE_REQUEST });

    return webApiClient
      .modifySampleType(data)
      .then((sampleType: SampleType) => {
        dispatch({
          type: MODIFY_ADMIN_SAMPLE_TYPE_SUCCESS,
          payload: sampleType,
        });
        notify.info({
          title: selMessage({ id: "admin.sample.type.updated.successTitle" }),
          children: SelMessage({
            id: "admin.sample.type.updated.successMessage",
            values: { sampleType: sampleType.name },
          }),
        });
        dispatch(loadSampleTypes());
        return sampleType;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: MODIFY_ADMIN_SAMPLE_TYPE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function validateParentTypeExists(name: string) {
  return (_dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient.validateParentTypeExists(name);
  };
}

export function saveNewParentType(config: InputParentTypeInput) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<ParentType> => {
    dispatch({ type: SAVE_ADMIN_PARENT_TYPE_REQUEST });

    return webApiClient
      .saveNewParentType(config)
      .then((parentType: ParentType) => {
        dispatch({
          type: SAVE_ADMIN_PARENT_TYPE_SUCCESS,
          payload: parentType,
        });
        notify.info({
          title: selMessage({ id: "admin.sample.parent.created.successTitle" }),
          children: SelMessage({
            id: "admin.sample.parent.created.successMessage",
            values: { parentType: parentType.name },
          }),
        });
        dispatch(loadParentTypes());
        return parentType;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SAVE_ADMIN_PARENT_TYPE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function modifyParentType(config: InputParentTypeInput) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<ParentType> => {
    dispatch({ type: MODIFY_ADMIN_PARENT_TYPE_REQUEST });

    return webApiClient
      .modifyParentType(config)
      .then((parentType: ParentType) => {
        dispatch({
          type: MODIFY_ADMIN_PARENT_TYPE_SUCCESS,
          payload: parentType,
        });
        notify.info({
          title: selMessage({ id: "admin.sample.parent.updated.successTitle" }),
          children: SelMessage({
            id: "admin.sample.parent.updated.successMessage",
            values: { parentType: parentType.name },
          }),
        });
        dispatch(loadParentTypes());
        return parentType;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: MODIFY_ADMIN_PARENT_TYPE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

/*** Utils ***/
export function getAttributeLabel(attribute: Attribute): string {
  return attribute.groupName ? `${attribute.groupName} - ${attribute.attributeName}` : attribute.attributeName;
}

export function isEmptyDefault(d: RawAttributeDefault): boolean {
  return !d || (!isDefinedNumber(d.defaultNumber) && !d.defaultText && !d.defaultTerm && !d.template);
}

export function getAttributeSuggestionValue(attribute: Attribute): string {
  return attribute ? getAttributeLabel(attribute) : null;
}

export function isDefinedNumber(value: number | undefined | null): boolean {
  return !!value || value === 0;
}
